import { type ChatWithConversationPane } from '@strata/react-chat';

import { fmhNpiAPI, USER_SUPPORT_API_URL } from 'api/APIBase';

// User
export async function registerCurrentUser({
    details,
}: {
    details: { first_name?: string; last_name?: string };
}) {
    const url = `${USER_SUPPORT_API_URL}/users/register`;
    return fmhNpiAPI({ url })
        .query({ update: true })
        .post(details)
        .json<{ upn: string; is_support_user: boolean; first_name?: string; last_name?: string }>();
}

// Conversations
export const FactDataTypes = {
    MESSAGE_TEMPLATE: 'MSG_TMPLT',
    PERFORMANCE_REF: 'PERF_REF',
    VALUE: 'VALUE',
} as const;
export type FactDataType = (typeof FactDataTypes)[keyof typeof FactDataTypes];

export const PerformanceTypes = {
    AUTOMATIC: 'AUTOMATIC',
    HYBRID: 'HYBRID',
    MANUAL: 'MANUAL',
} as const;
export type PerformanceType = (typeof PerformanceTypes)[keyof typeof PerformanceTypes];

export interface ConversationFact {
    datum_index?: number;
    datum_value?: string;
    label: string;
    data_type: FactDataType;
    shape: {
        type: string;
        options_dict?: Record<string | number, string>;
        helper_text?: string;
        placeholder_text?: string;
        show_other?: boolean;
    };
}

export interface ConversationMessage {
    conversation_id: string;
    message_id: string;
    sent_time: string;
    is_support_msg: boolean;
    first_name: string;
    last_name: string;
    upn?: string;
    content: {
        message?: string;
        status?: (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus];
        event_status?: string;
        completed_at?: string;
    };
    message_type: (typeof ChatWithConversationPane.MessageType)[keyof typeof ChatWithConversationPane.MessageType];
}

export interface Conversation {
    conversation_id: string;
    display_id: string;
    created_time: string;
    conversation_status: (typeof ChatWithConversationPane.ConversationStatus)[keyof typeof ChatWithConversationPane.ConversationStatus];
    closed_time?: string;
    closed_by_upn?: string;
    closed_by_first_name?: string;
    closed_by_last_name?: string;
    archived_time?: string;
    archived_by_upn?: string;
    archived_by_first_name?: string;
    archived_by_last_name?: string;
    created_by_support_user: boolean;
    short_title: string;
    first_fact: ConversationFact;
    assigned_upn?: string;
    assigned_first_name?: string;
    assigned_last_name?: string;
    supplier_npi: string;
    is_note: boolean;
    referral_id?: string;
    referral_friendly_id?: string;
    segment_id?: string;
    benefit?: string;
    member_first_name?: string;
    member_last_name?: string;
    topic_id?: string;
    details: {
        subject: string;
        data?: Record<string, string>;
        [FactDataTypes.PERFORMANCE_REF]?: Record<string, { name: string; type: PerformanceType }>;
    };
    created_by_upn: string;
    created_by_first_name?: string;
    created_by_last_name?: string;
    last_message_id?: string;
    messages?: ConversationMessage[];
    last_message?: ConversationMessage;
    last_updated?: string;
    is_unread?: boolean;
}

export interface NewConversationBody {
    details: {
        supplier_npi: string;
        is_note?: boolean;
        referral_id?: string;
        referral_friendly_id?: string;
        segment_id?: string;
        benefit?: string;
        member_first_name?: string;
        member_last_name?: string;
        topic_id?: string;
        details?: {
            subject?: string;
            data?: Record<string, string>;
            [FactDataTypes.PERFORMANCE_REF]?: Record<
                string,
                { name: string; type: PerformanceType }
            >;
        };
        created_by_upn: string;
        created_by_first_name?: string;
        created_by_last_name?: string;
    };
    data: {
        datum_id?: string;
        topic_id: string;
        schema_id: string;
        fact_id: string;
        datum_value?: string | null;
        datum_index?: number;
    }[];
}
